import { isFuseHolderVariant } from "./auto-fuse";
import type { DefLookup } from "./catalog";
import { effectiveTerminals } from "./effective";
import { hasConflictingInheritedFuse } from "./protection-devices";
import type {
  Cable,
  ComponentDefinition,
  FuseType,
  SystemNode,
  Terminal,
} from "./types";

/** A source-backed example, never a rating of an unidentified installed fuse. */
export type FuseReferenceProfile = {
  manufacturer: string;
  product: string;
  dcMaxVoltage: number;
  interruptRatings: readonly { upToVoltage: number; amperes: number }[];
  sourceUrl: string;
};

export const FUSE_REFERENCE_PROFILES: Record<FuseType, FuseReferenceProfile | undefined> = {
  ATC: { manufacturer: "Littelfuse", product: "ATOF 287", dcMaxVoltage: 32,
    interruptRatings: [{ upToVoltage: 32, amperes: 1000 }],
    sourceUrl: "https://www.littelfuse.com/assetdocs/littelfuse-datasheet-287-atof?assetguid=43dcdce8-8ca2-426f-8998-7e566f048d40" },
  MIDI: { manufacturer: "Littelfuse", product: "MIDI 498 32 V", dcMaxVoltage: 32,
    interruptRatings: [{ upToVoltage: 32, amperes: 2000 }],
    sourceUrl: "https://www.littelfuse.com/assetdocs/littelfuse_automotive_bolt_down_fuse_midi_32v?assetguid=b55e8034-180d-40f6-a0a7-bebc2d4a94f5" },
  MAXI: { manufacturer: "Littelfuse", product: "MAXI 32 V", dcMaxVoltage: 32,
    interruptRatings: [{ upToVoltage: 32, amperes: 1000 }],
    sourceUrl: "https://www.littelfuse.com/assetdocs/littelfuse-datasheet-maxi?assetguid=e69519f6-4d90-4977-88ca-53b342306667" },
  MEGA: { manufacturer: "Littelfuse", product: "MEGA 298 32 V", dcMaxVoltage: 32,
    interruptRatings: [{ upToVoltage: 32, amperes: 2000 }],
    sourceUrl: "https://www.littelfuse.com/products/fuses-overcurrent-protection/fuses/automotive-fuses/bolt-down-fuses/mega/298" },
  ANL: { manufacturer: "Blue Sea Systems", product: "ANL 5128", dcMaxVoltage: 80,
    interruptRatings: [{ upToVoltage: 80, amperes: 6000 }],
    sourceUrl: "https://www.bluesea.com/products/5128/ANL_Fuse_-_175_Amp" },
  MRBF: { manufacturer: "Blue Sea Systems", product: "MRBF 5183", dcMaxVoltage: 58,
    interruptRatings: [{ upToVoltage: 14, amperes: 10000 }, { upToVoltage: 32, amperes: 5000 }, { upToVoltage: 58, amperes: 2000 }],
    sourceUrl: "https://www.bluesea.com/products/5183/MRBF_Terminal_Fuse_-_100A" },
  ClassT: { manufacturer: "Blue Sea Systems", product: "Class T 5121", dcMaxVoltage: 125,
    interruptRatings: [{ upToVoltage: 125, amperes: 20000 }],
    sourceUrl: "https://www.bluesea.com/products/5121/Class_T_Fuse_-_400_Amp" },
  // NH identifies a form factor, not a DC rating. AC breaking capacity is not
  // evidence of DC interruption; no generic NH reference is assumed.
  NH: undefined,
  // gPV describes photovoltaic fuse utilization, not an exact installed
  // product. Its voltage and interrupt rating require the selected fuse.
  gPV: undefined,
  // The MDL data sheet lists AC interrupt ratings only.
  MDL: undefined,
};

/** Compatibility export for reference-only consumers. Never use to approve a fuse. */
export const FUSE_AIC = Object.fromEntries(
  Object.entries(FUSE_REFERENCE_PROFILES).map(([family, profile]) => [family, profile?.interruptRatings[0]?.amperes]),
) as Record<FuseType, number | undefined>;

export type ElectricalEvidence =
  | { status: "known"; value: number; basis: "declared" | "declared-parallel-sum" }
  | { status: "estimated"; value: number; basis: "ai-estimated" }
  | { status: "unknown"; reason: string };

/** The legacy scalar is a declared input, not a verified maximum fault current.
 * Chemistry, Ah, discharge limits and BMS trip currents cannot replace it. */
export function batteryFaultCurrent(args: Pick<ComponentDefinition,
  "shortCircuitA" | "aiEstimatedFields" | "source">): ElectricalEvidence {
  const value = args.shortCircuitA;
  if (value == null || !Number.isFinite(value) || value <= 0) {
    return { status: "unknown", reason: "missing-battery-fault-current" };
  }
  if (args.aiEstimatedFields?.includes("shortCircuitA") ||
      (args.source === "ai" && args.aiEstimatedFields == null)) {
    return { status: "estimated", value, basis: "ai-estimated" };
  }
  return { status: "known", value, basis: "declared" };
}

/** A reference comparison only. An absent result is unknown, never a pass.
 * Voltage limits apply to every profile, not only MRBF. */
export function fuseAicAtVoltage(type: FuseType, voltage: number): number | undefined {
  const profile = FUSE_REFERENCE_PROFILES[type];
  if (!profile || !Number.isFinite(voltage) || voltage <= 0 || voltage > profile.dcMaxVoltage) return undefined;
  return profile.interruptRatings.find(rating => voltage <= rating.upToVoltage)?.amperes;
}

/**
 * Reference current ranges from published example products. These endpoints
 * exclude unsupported automatic advice; an in-range rating does not establish
 * an available exact device, time-current behavior, voltage or interrupt rating.
 * Other manufacturers and the discrete ladders inside each series are not modeled.
 *
 * Sources, verified 2026-08-03:
 *   - ATC / ATO: Littelfuse ATOF/257, 1-40 A.
 *   - MIDI / AMI: Littelfuse 498, 30-200 A.
 *   - MAXI: Littelfuse 299, 20-80 A standard blade; 100 A for the indicator
 *     variants (MAXI Smart Glow, Bussmann BP/MAX-100ID).
 *   - MEGA / AMG: Littelfuse 298, 40-500 A.
 *   - ANL: Blue Sea ANL, 35-750 A.
 *   - MRBF: Blue Sea Marine Rated Battery Fuse, 30-300 A.
 *   - Class T: Littelfuse JLLN, 1-1200 A (DC voltage rating varies by amperage) — wider than the marine
 *     band (Blue Sea A3T, 110-400 A); neither range establishes DC suitability.
 *   - NH: Eaton Bussmann NH 500 V links, 2-1250 A, rated a.c. only.
 */
export const FUSE_AMP_RANGE: Record<FuseType, { min: number; max: number }> = {
  ATC: { min: 1, max: 40 },
  MIDI: { min: 30, max: 200 },
  MAXI: { min: 20, max: 100 },
  MEGA: { min: 40, max: 500 },
  ANL: { min: 35, max: 750 },
  MRBF: { min: 30, max: 300 },
  ClassT: { min: 1, max: 1200 },
  NH: { min: 2, max: 1250 },
  // Littelfuse SPF 10×38 photovoltaic fuse reference range, 1–30 A.
  // Other gPV formats have different ranges; this is not universal approval.
  // https://www.littelfuse.com/assetdocs/spf-series-datasheet?assetguid=6b937d56-50c3-4ea9-b12f-13cfd5f8f7d5
  gPV: { min: 1, max: 30 },
  // Eaton Bussmann MDL, data sheet 2004, ferrule-end series (1/16–30 A).
  MDL: { min: 1 / 16, max: 30 },
};

/** Reject only a recommendation outside the selected inline device family's
 * documented range. Passing this check establishes neither a purchasable
 * series rating nor time-current, voltage or interrupt suitability. A distinct
 * battery-post fuse does not select the family of a secondary inline device. */
export function cableFuseFamilyExcludesRating(
  cable: Cable,
  nodes: readonly SystemNode[],
  defLookup: DefLookup,
  rating: number,
): boolean {
  if (!(rating > 0)) return false;
  const excludes = (type: FuseType) => {
    // Imported JSON may predate or bypass runtime family validation. Unknown
    // identities (including object-prototype keys) provide no range evidence.
    if (typeof type !== "string" || !Object.hasOwn(FUSE_AMP_RANGE, type)) return false;
    const range = FUSE_AMP_RANGE[type];
    return rating < range.min || rating > range.max;
  };
  if (cable.fuseTypeOverride) return excludes(cable.fuseTypeOverride);
  if (hasConflictingInheritedFuse(cable, nodes, defLookup)) return false;
  return [
    { nodeId: cable.fromNodeId, termId: cable.fromTerminalId },
    { nodeId: cable.toNodeId, termId: cable.toTerminalId },
  ].some(end => {
    const node = nodes.find(n => n.id === end.nodeId);
    if (node?.kind !== "component") return false;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def?.fuseSlotType) return false;
    const terminal = effectiveTerminals(def, node).find(t => t.id === end.termId);
    return slotTypeAppliesAt(def, terminal) && excludes(def.fuseSlotType);
  });
}

/** Compatibility accessor: a missing declaration stays undefined, never zero. */
export function batteryShortCircuitA(args: Pick<ComponentDefinition, "shortCircuitA" | "capacityAh" | "chemistry">): number | undefined {
  const evidence = batteryFaultCurrent(args);
  return evidence.status === "unknown" ? undefined : evidence.value;
}

/**
 * Precedence: the cable's own override, then a `batteryFuse` on a battery+ it
 * touches, then an attached component's `fuseSlotType` where
 * `slotTypeAppliesAt`. Undefined ⇒ the fuse identity remains unresolved.
 */
export function resolveCableFuseType(
  cable: Cable,
  nodes: readonly SystemNode[],
  defLookup: DefLookup,
): { type: FuseType; kind: "override" | "inherited" } | undefined {
  if (cable.fuseTypeOverride) {
    return { type: cable.fuseTypeOverride, kind: "override" };
  }
  if (hasConflictingInheritedFuse(cable, nodes, defLookup)) return undefined;
  for (const end of [
    { nodeId: cable.fromNodeId, termId: cable.fromTerminalId },
    { nodeId: cable.toNodeId, termId: cable.toTerminalId },
  ]) {
    const n = nodes.find((nn) => nn.id === end.nodeId);
    if (n?.kind !== "component" || !n.batteryFuse?.type) continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (def?.category !== "battery") continue;
    const term = def.terminals.find((t) => t.id === end.termId);
    if (term?.polarity !== "positive") continue;
    return { type: n.batteryFuse.type, kind: "inherited" };
  }
  for (const end of [
    { nodeId: cable.fromNodeId, termId: cable.fromTerminalId },
    { nodeId: cable.toNodeId, termId: cable.toTerminalId },
  ]) {
    const n = nodes.find((nn) => nn.id === end.nodeId);
    if (n?.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def?.fuseSlotType) continue;
    const term = effectiveTerminals(def, n).find((t) => t.id === end.termId);
    if (slotTypeAppliesAt(def, term)) {
      return { type: def.fuseSlotType, kind: "inherited" };
    }
  }
  return undefined;
}

/**
 * Multi-slot block: output studs only. The feeder into `in-pos` is upstream of
 * every slot, and typing it from the block invents parts that don't exist (a
 * 125 A "ATC", where the family stops at 40 A). Inline single-slot holder: one
 * element in series, so either side inherits. Negative and ground legs never
 * inherit — ABYC E-11.10.1 protects the ungrounded conductor only.
 */
function slotTypeAppliesAt(
  def: ComponentDefinition,
  term: Terminal | undefined,
): boolean {
  if (!term) return false;
  if (term.polarity !== "positive") return false;
  if (isFuseHolderVariant(def)) return true;
  return term.direction === "output";
}

/** Stable display order; family names alone do not specify interrupt ratings. */
export const FUSE_TYPES_BY_AIC: FuseType[] = [
  "ATC",
  "MAXI",
  "MIDI",
  "MEGA",
  "ANL",
  "MRBF",
  "ClassT",
  "NH",
  "gPV",
  "MDL",
];

/** Families that bolt onto a battery post. Every other family ships as an
 *  inline holder — model those as a fuse-holder node, never as `batteryFuse`. */
export const BATTERY_TERMINAL_FUSE_TYPES: FuseType[] = ["MRBF", "ClassT"];

/** Technical names, deliberately not translated. */
export function fuseTypeDisplayName(type: FuseType): string {
  return type === "ClassT" ? "Class T" : type;
}
